/**
 * @fileoverview Cart Drawer Component
 * Handles cart drawer UI and interactions
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS LISTENED:
 * - cart:updated (from CartManager) → Updates drawer content
 * - cart:loaded (from CartManager)  → Updates drawer content
 * - cart:added (from CartManager)   → Updates drawer content
 * - click (drawer elements)         → Handles drawer interactions
 * - change (quantity inputs)        → Handles quantity changes
 * 
 * EVENTS DISPATCHED:
 * - drawer:opened  → Emitted when drawer opens
 * - drawer:closed  → Emitted when drawer closes
 * - drawer:updated → Emitted when drawer content updates
 * 
 * EVENT DATA STRUCTURE:
 * - drawer:opened/closed: { timestamp: number }
 * - drawer:updated: { cart: CartData, itemCount: number }
 */

import { SELECTORS, CSS_CLASSES, CART_EVENTS } from '../constants/index.js';
import { DOM, Debug, Performance } from '../utils/index.js';
import { DEFAULT_CONFIG } from '../constants/index.js';
import { QuantityManager } from '../core/QuantityManager.js';

export class CartDrawer {
    /**
     * CartDrawer class constructor
     */
    constructor(cartManager, config = {}) {
        if (!cartManager) {
            throw new Error('CartManager is required for CartDrawer');
        }

        this.manager = cartManager;
        this.config = config;
        this.elements = {};
        this.isOpen = false;
        this.imageObserver = null;
        this.updateQueue = new Map();
        this.isUpdating = false;
        this.lastUpdateTime = 0;
        this.MINIMUM_UPDATE_INTERVAL = 100; // 100ms minimum between updates

        // Initialize debounced update
        this.debouncedUpdate = Performance.debounce((cart, changedItemKey) => {
            this.processUpdate(cart, changedItemKey);
        }, 150); // 150ms debounce

        // Bind methods to preserve context
        this.toggle = this.toggle.bind(this);
        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleCartUpdate = this.handleCartUpdate.bind(this);
        this.handleItemAction = this.handleItemAction.bind(this);

        this.init();
    }

    /**
     * Initialize cart drawer
     * @private
     */
    init() {
        Debug.log('info', '[CartDrawer] Starting initialization');
        
        Performance.measure(() => {
            this.cacheElements();
            this.initImageObserver();
            this.bindEvents();
            this.setupAccessibility();
        }, 'drawerInit');
        
        // Try to get initial cart state
        const cartState = document.getElementById('CartState');
        if (cartState) {
            try {
                const initialCart = JSON.parse(cartState.textContent);
                if (initialCart && typeof initialCart === 'object') {
                    Debug.log('info', '[CartDrawer] Found initial cart state', {
                        itemCount: initialCart.item_count,
                        items: initialCart.items?.length || 0
                    });
                    this.processUpdate(initialCart);
                }
            } catch (error) {
                Debug.log('warn', '[CartDrawer] Failed to parse initial cart state', {
                    error,
                    content: cartState.textContent
                });
            }
        }
    }

    /**
     * Cache DOM elements
     * @private
     */
    cacheElements() {
        this.elements = {
            drawer: DOM.query(SELECTORS.CART_DRAWER),
            overlay: DOM.query(SELECTORS.CART_OVERLAY),
            closeBtn: DOM.query(SELECTORS.CART_CLOSE),
            itemsContainer: DOM.query(SELECTORS.CART_ITEMS),
            toggleButtons: DOM.queryAll(SELECTORS.CART_TOGGLE),
            countElements: DOM.queryAll(SELECTORS.CART_COUNT),
            totalElements: DOM.queryAll(SELECTORS.CART_TOTAL),
            subtotalElements: DOM.queryAll(SELECTORS.CART_SUBTOTAL)
        };
        
        Debug.log('info', '[CartDrawer] Elements cached', {
            drawer: !!this.elements.drawer,
            overlay: !!this.elements.overlay,
            closeBtn: !!this.elements.closeBtn,
            itemsContainer: !!this.elements.itemsContainer,
            toggleButtons: this.elements.toggleButtons.length,
            countElements: this.elements.countElements.length,
            totalElements: this.elements.totalElements.length,
            subtotalElements: this.elements.subtotalElements.length
        });
    }

    /**
     * Initialize intersection observer for lazy loading images
     * @private
     */
    initImageObserver() {
        this.imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    const src = img.dataset.src;
                    if (src) {
                        Performance.measure(() => {
                            img.src = src;
                            img.removeAttribute('data-src');
                            this.imageObserver.unobserve(img);
                        }, 'imageLazyLoad');
                    }
                }
            });
        }, {
            root: this.elements.itemsContainer,
            rootMargin: '50px',
            threshold: 0.1
        });
    }

    /**
     * Bind DOM events
     * @private
     */
    bindEvents() {
        Debug.log('info', '[CartDrawer] Binding events');
        
        // Toggle events
        this.elements.toggleButtons.forEach(button => {
            button.addEventListener('click', this.toggle);
        });

        // Close events
        if (this.elements.closeBtn) {
            this.elements.closeBtn.addEventListener('click', this.hide);
        }

        if (this.elements.overlay) {
            this.elements.overlay.addEventListener('click', this.hide);
        }

        // Keyboard events
        document.addEventListener('keydown', this.handleEscapeKey);

        // Cart update events
        this.manager.on(CART_EVENTS.UPDATED, this.handleCartUpdate);
        this.manager.on(CART_EVENTS.LOADED, this.handleCartUpdate);
        this.manager.on(CART_EVENTS.ADDED, (data) => {
            if (this.config.autoOpenDrawer && !this.isOpen) {
                this.show();
            }
            this.handleCartUpdate(data);
        });

        // Item actions
        if (this.elements.itemsContainer) {
            this.elements.itemsContainer.addEventListener('click', this.handleItemAction);
            this.elements.itemsContainer.addEventListener('change', this.handleItemAction);
        }
    }

    /**
     * Setup accessibility features
     * @private
     */
    setupAccessibility() {
        if (this.elements.drawer) {
            this.elements.drawer.setAttribute('role', 'dialog');
            this.elements.drawer.setAttribute('aria-label', 'Shopping cart');
            this.elements.drawer.setAttribute('aria-modal', 'true');
        }
    }

    /**
     * Process a single update
     * @private
     * @param {Cart} cart - Cart data
     * @param {string} [changedItemKey] - Key of changed item
     */
    async processUpdate(cart, changedItemKey) {
        if (!cart) return;

        // Prevent updates too close together
        const now = Date.now();
        if (now - this.lastUpdateTime < this.MINIMUM_UPDATE_INTERVAL) {
            Debug.log('info', '[CartDrawer] Skipping too-frequent update', {
                timeSinceLastUpdate: now - this.lastUpdateTime
            });
            return;
        }

        // Set updating flag and timestamp
        this.isUpdating = true;
        this.lastUpdateTime = now;

        try {
            Debug.log('info', '[CartDrawer] Processing update', {
                itemCount: cart.item_count,
                items: cart.items?.length || 0,
                changedItemKey
            });

            Performance.batchDOMUpdates(() => {
                // First, check if we need to re-render all items
                const itemsContainer = this.elements.itemsContainer;
                const currentItems = itemsContainer.querySelectorAll('[data-item-key]');
                const cartItemKeys = new Set(cart.items.map(item => item.key));
                const currentItemKeys = new Set(Array.from(currentItems).map(el => el.dataset.itemKey));

                // Re-render if item count mismatch or different items
                const needsRerender = currentItems.length !== cart.items.length ||
                    !Array.from(cartItemKeys).every(key => currentItemKeys.has(key));

                if (needsRerender) {
                    // Full re-render needed
                    this.renderItems(cart.items);
                } else if (changedItemKey) {
                    // Update specific item quantity
                    const changedItem = cart.items.find(item => item.key === changedItemKey);
                    if (changedItem) {
                        this.updateItemQuantity(changedItem);
                    }
                } else {
                    // Update all quantities
                    cart.items.forEach(item => {
                        this.updateItemQuantity(item);
                    });
                }

                // Update counts, prices, and state
                this.updateCartState(cart);
            });

        } catch (error) {
            Debug.log('error', '[CartDrawer] Update failed', {
                error,
                stackTrace: error.stack
            });
        } finally {
            this.isUpdating = false;
        }
    }

    /**
     * Update cart state (counts, prices, empty state)
     * @private
     * @param {Cart} cart - Cart data
     */
    updateCartState(cart) {
        // Update counts
        this.elements.countElements.forEach(el => {
            const newCount = cart.item_count.toString();
            if (el.textContent !== newCount) {
                el.textContent = newCount;
                el.setAttribute('data-count', newCount);
            }
        });

        // Update totals
        this.elements.totalElements.forEach(el => {
            const newTotal = this.formatMoney(cart.total_price);
            if (el.textContent !== newTotal) {
                el.textContent = newTotal;
                el.setAttribute('data-total', cart.total_price);
            }
        });

        // Update subtotals
        this.elements.subtotalElements.forEach(el => {
            const newSubtotal = this.formatMoney(cart.items_subtotal_price);
            if (el.textContent !== newSubtotal) {
                el.textContent = newSubtotal;
                el.setAttribute('data-subtotal', cart.items_subtotal_price);
            }
        });

        // Update empty state
        if (this.elements.drawer) {
            this.elements.drawer.classList.toggle(CSS_CLASSES.IS_EMPTY, cart.item_count === 0);
        }
    }

    /**
     * Update quantity inputs for a specific item
     * @private
     * @param {CartItem} item - Cart item to update
     * @param {NodeList} [cachedInputs] - Pre-queried inputs for optimization
     */
    updateItemQuantity(item, cachedInputs) {
        const inputs = cachedInputs || document.querySelectorAll(
            `[data-quantity-input][data-item-key="${item.key}"], [data-quantity-display][data-item-key="${item.key}"]`
        );

        if (inputs.length === 0) return;

        Debug.log('info', '[CartDrawer UI] Updating quantity inputs for item', {
            itemKey: item.key,
            quantity: item.quantity,
            inputCount: inputs.length
        });

        const newValue = item.quantity.toString();
        inputs.forEach(element => {
            const currentValue = element.tagName === 'INPUT' ? element.value : element.textContent;
            
            if (currentValue !== newValue) {
                if (element.tagName === 'INPUT') {
                    element.value = newValue;
                } else {
                    element.textContent = newValue;
                }
                element.classList.add('cart-element-updated');
                setTimeout(() => element.classList.remove('cart-element-updated'), 300);
            }
        });

        // Update minus buttons state
        const minusButtons = document.querySelectorAll(
            `[data-quantity-button="decrease"][data-item-key="${item.key}"]`
        );
        minusButtons.forEach(button => {
            button.disabled = item.quantity <= 1;
        });
    }

    /**
     * Handle cart update event
     * @param {Object} data - Event data
     */
    handleCartUpdate(data) {
        const cart = data.cart || this.manager.cart;
        console.log('[CartDrawer] handleCartUpdate', { hasCart: !!cart, item_count: cart?.item_count, operationId: data?.operationId });
        if (!cart) return;

        // Extract changed item key from operation ID
        let changedItemKey = null;
        if (data.operationId) {
            const match = data.operationId.match(/changeQuantity_(\d+)_/);
            if (match) {
                const variantId = Number(match[1]);
                const item = cart.items.find(i => i.variant_id === variantId);
                if (item) {
                    changedItemKey = item.key;
                }
            }
        }

        // Queue update through debouncer
        this.debouncedUpdate(cart, changedItemKey);
    }

  /**
   * Handle item action (quantity change or remove)
   * @param {Event} event - Click or change event
   */
  async handleItemAction(event) {
    const target = event.target;
    
    // Prevent multiple simultaneous actions on the same item
    if (target.classList.contains(CSS_CLASSES.IS_LOADING)) {
      Debug.log('warn', '[CartDrawer] Action blocked - element is already loading', {
        target: target.tagName,
        targetClasses: target.className
      });
      return;
    }
    
    Debug.log('info', '[CartDrawer] Item action event received', {
      target: target.tagName,
      targetClasses: target.className,
      targetDataset: target.dataset,
      eventType: event.type
    });

    // Handle quantity input changes
    if (target.matches('input[data-quantity-input]')) {
      // Skip if input is disabled (linked items with _for property)
      if (target.disabled || target.readOnly) {
        return;
      }
      
      const itemKey = target.dataset.itemKey;
      const newQuantity = parseInt(target.value, 10);
      
      if (!itemKey || isNaN(newQuantity)) {
        Debug.log('warn', '[CartDrawer] Invalid input data', {
          itemKey,
          value: target.value
        });
        return;
      }

      // Add loading state to all related elements
      const itemElements = document.querySelectorAll(`[data-item-key="${itemKey}"]`);
      itemElements.forEach(el => {
        el.classList.add(CSS_CLASSES.IS_LOADING);
        if (el.tagName === 'BUTTON') {
          el.disabled = true;
        }
      });

      try {
        // Get current cart to find the item
        const cart = await this.manager.getCart();
        const item = cart.items.find(i => i.key === itemKey);
        
        if (!item) {
          Debug.log('warn', '[CartDrawer] Item not found in cart', {
            itemKey,
            cartItems: cart.items.map(i => i.key)
          });
          return;
        }

        if (newQuantity <= 0) {
          await this.removeItem(itemKey);
        } else {
          // Update the cart using variant_id
          const updatedCart = await this.manager.changeItemQuantity(item.variant_id, newQuantity);
          
          // Force a UI update with the new cart data
          this.updateDrawer(updatedCart, itemKey);
        }
      } catch (error) {
        Debug.log('error', '[CartDrawer] Failed to update quantity via input', {
          error,
          itemKey,
          newQuantity,
          stackTrace: error.stack
        });
        
        // Revert the input value
        const cart = await this.manager.getCart();
        const item = cart.items.find(i => i.key === itemKey);
        if (item) {
          target.value = item.quantity;
          this.updateDrawer(cart, itemKey);
        }
      } finally {
        // Remove loading state from all related elements
        itemElements.forEach(el => {
          el.classList.remove(CSS_CLASSES.IS_LOADING);
          if (el.tagName === 'BUTTON') {
            el.disabled = false;
          }
        });

        // Additional safety: Clear any lingering loading states for this item
        setTimeout(() => {
          const safetyElements = document.querySelectorAll(`[data-item-key="${itemKey}"]`);
          safetyElements.forEach(el => {
            if (el.classList.contains(CSS_CLASSES.IS_LOADING)) {
              el.classList.remove(CSS_CLASSES.IS_LOADING);
              if (el.tagName === 'BUTTON') {
                el.disabled = false;
              }
            }
          });
        }, 100);
      }
      
      return;
    }

    // Handle button actions (plus, minus, remove)
    const button = target.closest('button[data-item-key]');
    if (!button) {
      Debug.log('debug', '[CartDrawer] No actionable button clicked', {
        target: target.outerHTML
      });
      return;
    }
    
    // Skip if button is disabled (linked items with _for property)
    // Exception: allow remove button to work
    if (button.disabled && button.dataset.quantityButton !== 'remove') {
      return;
    }

    const itemKey = button.dataset.itemKey;
    const action = button.dataset.quantityButton;

    if (!itemKey) {
      Debug.log('warn', '[CartDrawer] Button missing item key', {
        button: button.outerHTML
      });
      return;
    }

    Debug.log('info', '[CartDrawer] Processing button action', {
      itemKey,
      action,
      buttonData: button.dataset
    });

    // Add loading state to all related elements
    const itemElements = document.querySelectorAll(`[data-item-key="${itemKey}"]`);
    const originalButtonText = button.textContent;
    
    itemElements.forEach(el => {
      el.classList.add(CSS_CLASSES.IS_LOADING);
      if (el.tagName === 'BUTTON') {
        el.disabled = true;
        if (el === button) {
          el.textContent = 'Loading...';
        }
      }
    });

    try {
      let updatedCart;
      
      switch (action) {
        case 'increase':
          Debug.log('info', '[CartDrawer] Increasing quantity', { itemKey });
          updatedCart = await this.updateQuantity(itemKey, 1);
          break;
        case 'decrease':
          Debug.log('info', '[CartDrawer] Decreasing quantity', { itemKey });
          updatedCart = await this.updateQuantity(itemKey, -1);
          break;
        case 'remove':
          Debug.log('info', '[CartDrawer] Removing item', { itemKey });
          updatedCart = await this.removeItem(itemKey);
          break;
        default:
          Debug.log('warn', '[CartDrawer] Unknown button action', {
            action,
            button: button.outerHTML
          });
          return;
      }
      
      // Force a UI update with the new cart data
      if (updatedCart) {
        this.updateDrawer(updatedCart, itemKey);
      }
    } catch (error) {
      Debug.log('error', '[CartDrawer] Failed to perform item action', {
        error,
        itemKey,
        action: button.dataset,
        stackTrace: error.stack
      });
      
      // Show error state briefly
      button.classList.add(CSS_CLASSES.IS_ERROR);
      button.textContent = 'Error';
      setTimeout(() => {
        button.classList.remove(CSS_CLASSES.IS_ERROR);
        button.textContent = originalButtonText;
      }, 2000);
    } finally {
      // Remove loading state from all related elements
      itemElements.forEach(el => {
        el.classList.remove(CSS_CLASSES.IS_LOADING);
        if (el.tagName === 'BUTTON') {
          el.disabled = false;
          if (el === button && !el.classList.contains(CSS_CLASSES.IS_ERROR)) {
            el.textContent = originalButtonText;
          }
        }
      });

      // Additional safety: Clear any lingering loading states for this item
      setTimeout(() => {
        const safetyElements = document.querySelectorAll(`[data-item-key="${itemKey}"]`);
        safetyElements.forEach(el => {
          if (el.classList.contains(CSS_CLASSES.IS_LOADING)) {
            el.classList.remove(CSS_CLASSES.IS_LOADING);
            if (el.tagName === 'BUTTON') {
              el.disabled = false;
              // Restore button text if needed
              const action = el.dataset.quantityButton;
              if (el.textContent === 'Loading...') {
                if (action === 'increase') {
                  el.textContent = '+';
                } else if (action === 'decrease') {
                  el.textContent = '−';
                } else if (action === 'remove') {
                  el.textContent = 'Remove';
                }
              }
            }
          }
        });
      }, 100);
    }
  }

  setupAccessibility() {
    const drawer = this.elements.drawer;
    if (!drawer) return;

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Shopping Cart');
    drawer.setAttribute('aria-hidden', 'true');
  }

  async updateQuantity(itemKey, change) {
    try {
      // Get current cart to find the item
      const cart = await this.manager.getCart();
      const item = cart.items.find(i => i.key === itemKey);
      
      if (!item) {
        Debug.log('warn', '[CartDrawer] Item not found in cart', {
          itemKey,
          cartItems: cart.items.map(i => i.key)
        });
        return null;
      }

      const newQuantity = item.quantity + change;
      if (newQuantity < 0) {
        Debug.log('warn', '[CartDrawer] Invalid quantity', {
          itemKey,
          currentQuantity: item.quantity,
          change,
          newQuantity
        });
        return null;
      }

      Debug.log('info', '[CartDrawer] Updating item quantity', {
        itemKey,
        variantId: item.variant_id,
        productTitle: item.product_title,
        currentQuantity: item.quantity,
        newQuantity
      });

      if (newQuantity === 0) {
        return await this.removeItem(itemKey);
      } else {
        const updatedCart = await this.manager.changeItemQuantity(item.variant_id, newQuantity);
        
        Debug.log('info', '[CartDrawer] Item quantity updated successfully', {
          itemKey,
          variantId: item.variant_id,
          newQuantity,
          cartItemCount: updatedCart.item_count
        });
        
        return updatedCart;
      }
    } catch (error) {
      Debug.log('error', '[CartDrawer] Failed to update item quantity', {
        error,
        itemKey,
        change,
        stackTrace: error.stack
      });
      throw error;
    }
  }

  async removeItem(itemKey) {
    try {
      // Get current cart to find the item
      const cart = await this.manager.getCart();
      const item = cart.items.find(i => i.key === itemKey);
      
      if (!item) {
        Debug.log('warn', '[CartDrawer] Item not found in cart', {
          itemKey,
          cartItems: cart.items.map(i => i.key)
        });
        return null;
      }

      Debug.log('info', '[CartDrawer] Removing item', {
        itemKey,
        variantId: item.variant_id,
        productTitle: item.product_title
      });

      const updatedCart = await this.manager.changeItemQuantity(item.variant_id, 0);
      
      Debug.log('info', '[CartDrawer] Item removed successfully', {
        itemKey,
        variantId: item.variant_id,
        cartItemCount: updatedCart.item_count
      });
      
      return updatedCart;
    } catch (error) {
      Debug.log('error', '[CartDrawer] Failed to remove item', {
        error,
        itemKey,
        stackTrace: error.stack
      });
      throw error;
    }
  }

  /**
   * Update drawer contents and all quantity inputs across the site
   * @param {Cart} [cart] - Cart data to render
   * @param {string} [changedItemKey] - Key of the item that changed (optional)
   * @private
   */
  updateDrawer(cart = null, changedItemKey = null) {
    Debug.log('info', '[CartDrawer UI] updateDrawer called', {
        hasCart: !!cart,
        hasManagerCart: !!this.manager?.cart,
        changedItemKey
    });

    cart = cart || this.manager.cart;
    if (!cart) {
        Debug.log('warn', '[CartDrawer UI] No cart data available for update');
        return;
    }

    Debug.log('info', '[CartDrawer] Starting drawer update', {
        itemCount: cart.item_count,
        items: cart.items?.length || 0,
        changedItemKey
    });

    // Performance optimization: Only update quantities for changed item
    Performance.batchDOMUpdates(() => {
        // Check if we need to re-render all items
        const needsRerender = this.shouldRerenderItems(cart, changedItemKey);
        
        if (needsRerender) {
            // Full re-render needed (item removed, added, or cart structure changed)
            Debug.log('info', '[CartDrawer] Re-rendering all items', {
                reason: 'items structure changed',
                itemCount: cart.items.length
            });
            this.renderItems(cart.items);
        } else if (changedItemKey) {
            // Update only the changed item
            const changedItem = cart.items.find(item => item.key === changedItemKey);
            if (changedItem) {
                this.updateItemQuantity(changedItem);
            }
        } else {
            // Full update for all items (initial load or clear)
            cart.items.forEach(item => this.updateItemQuantity(item));
        }

        // Update counts
        this.elements.countElements.forEach(el => {
            el.textContent = cart.item_count;
            el.setAttribute('data-count', cart.item_count);
        });

        // Update totals
        this.elements.totalElements.forEach(el => {
            el.textContent = this.formatMoney(cart.total_price);
            el.setAttribute('data-total', cart.total_price);
        });

        // Update subtotals
        this.elements.subtotalElements.forEach(el => {
            el.textContent = this.formatMoney(cart.items_subtotal_price);
            el.setAttribute('data-subtotal', cart.items_subtotal_price);
        });

        // Update empty state
        if (this.elements.drawer) {
            this.elements.drawer.classList.toggle(CSS_CLASSES.IS_EMPTY, cart.item_count === 0);
        }
    });

    Debug.log('info', '[CartDrawer] Drawer update completed', {
        itemCount: cart.item_count,
        totalPrice: cart.total_price,
        itemsRendered: cart.items?.length || 0,
        changedItemKey
    });
  }

  /**
   * Determine if items need to be re-rendered
   * @private
   * @param {Cart} cart - Cart data
   * @param {string} [changedItemKey] - Key of the item that changed
   * @returns {boolean} True if re-render is needed
   */
  shouldRerenderItems(cart, changedItemKey) {
    if (!this.elements.itemsContainer) return false;

    // Get current items in DOM
    const currentItems = this.elements.itemsContainer.querySelectorAll('[data-item-key]');
    const currentItemKeys = new Set(Array.from(currentItems).map(el => el.dataset.itemKey));
    const cartItemKeys = new Set(cart.items.map(item => item.key));

    // Check if item count changed
    if (currentItems.length !== cart.items.length) {
      Debug.log('info', '[CartDrawer] Item count changed, re-render needed', {
        currentCount: currentItems.length,
        cartCount: cart.items.length
      });
      return true;
    }

    // Check if items are different
    const hasDifferentItems = !Array.from(cartItemKeys).every(key => currentItemKeys.has(key));
    if (hasDifferentItems) {
      Debug.log('info', '[CartDrawer] Items changed, re-render needed', {
        currentKeys: Array.from(currentItemKeys),
        cartKeys: Array.from(cartItemKeys)
      });
      return true;
    }

    // Check if changed item no longer exists (was removed)
    if (changedItemKey && !cartItemKeys.has(changedItemKey)) {
      Debug.log('info', '[CartDrawer] Changed item was removed, re-render needed', {
        removedItemKey: changedItemKey
      });
      return true;
    }

    return false;
  }

/**
 * Update quantity inputs for a specific item
 * @private
 * @param {CartItem} item - Cart item to update
 */
updateItemQuantity(item) {
    // Use a more specific selector and cache the NodeList
    const selector = `[data-quantity-input][data-item-key="${item.key}"], [data-quantity-display][data-item-key="${item.key}"]`;
    const allQuantityInputs = document.querySelectorAll(selector);

    if (allQuantityInputs.length === 0) return;

    Debug.log('info', '[CartDrawer UI] Updating quantity inputs for item', {
        itemKey: item.key,
        quantity: item.quantity,
        inputCount: allQuantityInputs.length
    });

    allQuantityInputs.forEach(element => {
        const currentValue = element.tagName === 'INPUT' ? element.value : element.textContent;
        
        if (currentValue !== item.quantity.toString()) {
            if (element.tagName === 'INPUT') {
                element.value = item.quantity;
            } else {
                element.textContent = item.quantity;
            }

            // Add update animation
            element.classList.add('cart-element-updated');
            setTimeout(() => element.classList.remove('cart-element-updated'), 300);
        }
    });

    // Update minus buttons state
    const minusButtons = document.querySelectorAll(
        `[data-quantity-button="decrease"][data-item-key="${item.key}"]`
    );
    minusButtons.forEach(button => {
        button.disabled = item.quantity <= 1;
    });

    // Clear any lingering loading states for this item
    const itemElements = document.querySelectorAll(`[data-item-key="${item.key}"]`);
    itemElements.forEach(el => {
        el.classList.remove(CSS_CLASSES.IS_LOADING);
        if (el.tagName === 'BUTTON') {
            el.disabled = false;
            // Restore original button text if it was changed to "Loading..."
            if (el.textContent === 'Loading...') {
                const action = el.dataset.quantityButton;
                if (action === 'increase') {
                    el.textContent = '+';
                } else if (action === 'decrease') {
                    el.textContent = '−';
                } else if (action === 'remove') {
                    el.textContent = 'Remove';
                }
            }
        }
    });
}

  /**
   * Render cart items
   * @private
   * @param {CartItem[]} items - Cart items to render
   */
  renderItems(items) {
    Debug.log('info', '[CartDrawer] Rendering cart items', {
      itemCount: items.length
    });

    const itemsHtml = items.map(item => {
      // Check if item has _for property (linked to main product)
      // Handle both object and array formats for properties
      let hasForProperty = false;
      try {
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            hasForProperty = item.properties.some(prop => prop && prop.name === '_for' && prop.value);
          } else if (typeof item.properties === 'object') {
            hasForProperty = !!item.properties._for;
          }
        }
      } catch (error) {
        Debug.log('warn', '[CartDrawer] Error checking _for property:', error);
        hasForProperty = false;
      }
      
      return `
      <div class="cart-item" data-item-key="${item.key}">
        <div class="cart-item__image">
          ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
        </div>
        <div class="cart-item__content">
          <h3 class="cart-item__title">
            <a href="${item.url}">${item.product_title}</a>
          </h3>
          ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <div class="cart-item__price">
            ${this.formatMoney(item.final_price)}
          </div>
          <div class="cart-item__quantity">
            <!-- Quantity controls (disabled if linked item with _for property) -->
            <button 
              type="button" 
              class="quantity-button quantity-minus" 
              data-quantity-button="decrease" 
              data-item-key="${item.key}"
              aria-label="Decrease quantity"
              ${hasForProperty || item.quantity <= 1 ? 'disabled' : ''}
              style="${hasForProperty ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
            >−</button>
            <input 
              type="number" 
              class="quantity-input" 
              value="${item.quantity}" 
              min="1" 
              data-quantity-input 
              data-item-key="${item.key}"
              aria-label="Item quantity"
              ${hasForProperty ? 'disabled readonly' : ''}
              style="${hasForProperty ? 'opacity: 0.7; cursor: not-allowed; pointer-events: none; background-color: #f3f4f6;' : ''}"
            />
            <button 
              type="button" 
              class="quantity-button quantity-plus" 
              data-quantity-button="increase" 
              data-item-key="${item.key}"
              aria-label="Increase quantity"
              ${hasForProperty ? 'disabled' : ''}
              style="${hasForProperty ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
            >+</button>
            <button 
              type="button" 
              class="cart-item__remove" 
              data-quantity-button="remove" 
              data-item-key="${item.key}"
              aria-label="Remove ${item.product_title} from cart"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    `;
    }).join('');

    this.elements.itemsContainer.innerHTML = items.length ? itemsHtml : `
      <div class="cart-empty">
        <p>Your cart is empty</p>
      </div>
    `;

    // Note: Event listeners are handled by the main handleItemAction method
    // No need to add duplicate listeners here

    Debug.log('info', '[CartDrawer] Cart items rendered', {
      itemCount: items.length,
      containerUpdated: true,
      buttons: {
        plus: this.elements.itemsContainer.querySelectorAll('[data-quantity-button="increase"]').length,
        minus: this.elements.itemsContainer.querySelectorAll('[data-quantity-button="decrease"]').length,
        remove: this.elements.itemsContainer.querySelectorAll('[data-quantity-button="remove"]').length,
        inputs: this.elements.itemsContainer.querySelectorAll('[data-quantity-input]').length
      }
    });
  }

  /**
   * Show cart drawer
   */
  show() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    if (this.elements.drawer) {
      this.elements.drawer.classList.add(CSS_CLASSES.IS_VISIBLE);
      this.elements.drawer.setAttribute('aria-hidden', 'false');
    }
    if (this.elements.overlay) {
      this.elements.overlay.classList.add(CSS_CLASSES.IS_VISIBLE);
    }
    document.body.classList.add(CSS_CLASSES.CART_OPEN);
    document.body.style.overflow = 'hidden';
    
    // Trigger any necessary updates
    if (this.manager.cart) {
        this.processUpdate(this.manager.cart);
    }
  }

  /**
   * Hide cart drawer
   */
  hide() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    if (this.elements.drawer) {
      this.elements.drawer.classList.remove(CSS_CLASSES.IS_VISIBLE);
      this.elements.drawer.setAttribute('aria-hidden', 'true');
    }
    if (this.elements.overlay) {
      this.elements.overlay.classList.remove(CSS_CLASSES.IS_VISIBLE);
    }
    document.body.classList.remove(CSS_CLASSES.CART_OPEN);
    document.body.style.overflow = '';
  }

  /**
   * Toggle cart drawer
   * @param {Event} event - Click event
   */
  toggle(event) {
    event?.preventDefault();
    
    // Check global A/B testing variable for cart redirect behavior
    if (window.cartRedirectToPage === true) {
      // Redirect to cart page instead of toggling drawer
      window.location.href = '/cart';
      return;
    }
    
    this.isOpen ? this.hide() : this.show();
  }

  /**
   * Handle escape key press
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.hide();
    }
  }

  formatMoney(cents) {
    // Get currency from Shopify context
    const currency = window.Shopify?.currency?.active || 
                    window.Shopify?.shop?.currency || 
                    document.documentElement.getAttribute('data-currency') ||
                    'USD';
    
    // Get locale from Shopify context or use browser default
    const locale = window.Shopify?.locale || 
                  document.documentElement.getAttribute('data-locale') ||
                  navigator.language ||
                  'en-US';
    
    try {
      return (cents / 100).toLocaleString(locale, {
        style: 'currency',
        currency: currency
      });
    } catch (error) {
      // Fallback to USD if currency formatting fails
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }
  }

  /**
   * Clean up drawer
   */
  destroy() {
    if (this.imageObserver) {
      this.imageObserver.disconnect();
    }
    
    if (this.updateFrame) {
      cancelAnimationFrame(this.updateFrame);
    }
    
    this.pendingUpdates.clear();
    this.elements = {};
    
    Debug.log('info', '[CartDrawer] Destroyed');
  }

  /**
   * Disable cart drawer functionality (used when on cart page)
   */
  disable() {
    this.isDisabled = true;
    
    // Hide the drawer if it's open
    if (this.isOpen) {
      this.hide();
    }
    
    // Remove event listeners
    if (this.elements.drawer) {
      this.elements.drawer.removeEventListener('click', this.handleItemAction);
    }
    
    if (this.elements.overlay) {
      this.elements.overlay.removeEventListener('click', this.hide);
    }
    
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleEscapeKey);
    
    Debug.log('info', '[CartDrawer] Disabled');
  }
} 